import React, {FC, useState, useEffect } from 'react';
import axios from 'axios';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Card } from 'react-bootstrap';
import * as Yup from "yup";
import ConfirmationModal from '../shared/ConfirmationModal';
import NewconfirmationModal from '../shared/NewconfirmationModal';
import AllFailureModal from '../shared/AllFailureModal';

interface Company {
  company_id: string;
  company_name: string;
}

const HrAdd: FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
  const [companySearch, setCompanySearch] = useState("");
  const [errorfetch, setError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [failureMessage, setFailureMessage] = useState("");
  const [modalShow, setModalShow] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [status, setStatus] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}companyDetails/companylist`);
      setCompanies(response.data.companies || response?.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setError("Failed to fetch companies. Please try again later.");
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    mobile: Yup.string().required("Mobile number is required"),
    password: Yup.string().required("Password is required"),
    linkedinid: Yup.string().url("Invalid URL"),
    designation: Yup.string().required("Designation is required"),
    company_id: Yup.string().required("Company is required"),
  });

  const handleSearch = (value: string) => {
    setCompanySearch(value);

    if (value.trim() === "") {
      setFilteredCompanies([]); 
      return;
    }

    const filtered = companies.filter(company =>
      company.company_name.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredCompanies(filtered);
  };

  const handleSelect = (company: Company, setFieldValue: (field: string, value: any) => void) => {
    setFieldValue("company_id", company.company_id);
    setCompanySearch(company.company_name);
    setFilteredCompanies([]);
  };

  const uniqueFilteredCompanies = Array.from(
    new Map(filteredCompanies.map(item => [item.company_id, item])).values()
  );

  const callApiHrAdd = async (values: any) => {
    setModalMessage("Are you sure you want to add this HR?");
    setPendingAction(() => async () => {
    try {
        const url = `${process.env.REACT_APP_API_URL}hr_details/createhr`;
        const response = await axios.post(url, values);
        console.log("API Response:", response.data);
        if (response.status === 201) {
            // toast.success('HR Created Successfully');
            setSuccessMessage("HR added successfully");
            setStatus(true);
            setShowSuccessModal(true);
            setTimeout(() => navigate('/hrlist'), 2000);
        }
    } catch (error: any) {
        console.error("API Error:", error.response?.data || error.message);
        setFailureMessage(error.response?.data?.message||"Failed to create HR record.");
        setShowFailureModal(true);
        // toast.error(error.response?.data?.message || "Failed to create HR record.");
    }
  });
    setModalShow(true);
  };

  const handleModalOk = () => {
    setModalShow(false);

    if (pendingAction) {
      pendingAction();
    }
    
    if (status) {
      const token = localStorage.getItem('jwttoken');
      if (token) {
        navigate('/hrlist')
      }
      return {
        error: false
      }
    }
    }

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="shadow">
            <Card.Header className="bg-info text-white text-center">
              <h4>Create New HR</h4>
            </Card.Header>
            <Card.Body>
              <Formik
                initialValues={{
                  name: "",
                  email: "",
                  mobile: "",
                  password: "",
                  linkedinid: "",
                  designation: "",
                  company_id: "",
                }}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={async (values, { setSubmitting }) => {
                  try {
                    await callApiHrAdd(values);
                  } finally {
                    setSubmitting(false);
                  }
                }} 
              >
                {({ values, setFieldValue, touched, errors }) => (
                  <Form>
                    <Row className="mb-3">
                      <Col md={12}>
                        <label className="form-label">Name</label>
                        <Field name="name" type="text" className="form-control" />
                        <ErrorMessage name="name" component="div" className="text-danger" />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={12}>
                        <label className="form-label">Email</label>
                        <Field name="email" type="email" className="form-control" />
                        <ErrorMessage name="email" component="div" className="text-danger" />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={12}>
                        <label className="form-label">Mobile</label>
                        <Field name="mobile" type="text" className="form-control" />
                        <ErrorMessage name="mobile" component="div" className="text-danger" />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={12}>
                        <label className="form-label">Password</label>
                        <div className="position-relative">
                          <Field
                            name="password"
                            type={showPassword ? "text" : "password"}
                            className="form-control pe-5"
                            autoComplete="new-password"
                          />
                          <i
                            className={`position-absolute top-50 translate-middle-y end-0 me-3 fas ${
                              showPassword ? "fa-eye-slash" : "fa-eye"
                            }`}
                            style={{ cursor: "pointer" }}
                            onClick={() => setShowPassword(!showPassword)}
                          ></i>
                        </div>
                        <ErrorMessage name="password" component="div" className="text-danger" />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={12}>
                        <label className="form-label">Linkedin ID</label>
                        <Field name="linkedinid" type="linkedinid" className="form-control" />
                        <ErrorMessage name="linkedinid" component="div" className="text-danger" />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={12}>
                        <label className="form-label">Designation</label>
                        <Field name="designation" type="designation" className="form-control" />
                        <ErrorMessage name="designation" component="div" className="text-danger" />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={12}>
                        <label className="form-label">Company</label>
                        <Field name="company_id" type="hidden" />
                        <input
                          type="text"
                          className="form-control"
                          value={companySearch}
                          onChange={(e) => handleSearch(e.target.value)}
                          placeholder="Search Company"
                          autoComplete="off"
                        />
                        {uniqueFilteredCompanies.length > 0 && (
                          <ul className="list-group position-absolute w-100">
                            {uniqueFilteredCompanies.map((company) => (
                              <li
                                key={company.company_id}
                                className="list-group-item list-group-item-action"
                                onClick={() => handleSelect(company, setFieldValue)}
                              >
                                {company.company_name}
                              </li>
                            ))}
                          </ul>
                        )}
              {errorfetch && <p className="text-danger">{errorfetch}</p>}

{!errorfetch && (
  <ErrorMessage name="company_id">
    {(msg) => msg && <div className="text-danger">{msg}</div>}
  </ErrorMessage>
)}

                      </Col>
                    </Row>
              <div className="row">
              <div className="text-center my-4">
                <button type="submit" className="btn btn-success">
                  Add HR
                </button>
                <button  className="btn btn-danger ms-3" onClick={() => navigate(-1)}>
                  Go Back
                </button>
              </div>
            </div>
                    {/* <Row className="mt-4 text-center">
                      <Col>
                        <Button variant="danger" onClick={() => navigate(-1)}>Cancel</Button>
                      </Col>
                      <Col>
                        <Button type="submit" variant="success">Submit</Button>
                      </Col>
                    </Row> */}
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
      </Row>
       <ConfirmationModal
                      show={modalShow}
                      modalMessage={modalMessage}
                      onHide={() => setModalShow(false)}
                      confirmation={handleModalOk}
                      operationType="Confirm"
                    />
            <NewconfirmationModal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} modalMessage={successMessage} operationType="Ok" timeoutDuration={3000} />
      <AllFailureModal show={showFailureModal} onHide={() => setShowFailureModal(false)} modalMessage={failureMessage}  operationType="Ok" timeoutDuration={5000}/>
           
    </Container>
  );
};

export default HrAdd;